import React from "react";
import * as Styled from "./styles";
import { useClinicalBudget } from "shared/providers/ClinicalBudget";
import { useAuth } from "shared/providers/auth";
import { getOpcoesValor } from "requests/ClinicalBudget";
import { ICategoria } from "shared/types/ClinicalBudget";
import { IoClose, IoCheckmarkCircle, IoFolderOpen } from "react-icons/io5";

const BudgetGruposModal: React.FC = () => {
  const { user } = useAuth();
  const {
    showCategoriasModal,
    setShowCategoriasModal,
    selectedProc,
    setSelectedProc,
    categorias,
    setCategorias,
    loadingOpcoes,
    setLoadingOpcoes,
    setSelectedCategoria,
    setValorPadrao,
    setSelectedValor,
    setSelectedCatprocId,
    setShowOdontograma,
    setShowArcadaModal,
    setShowObsModal,
    formatMoeda,
    showValues,
  } = useClinicalBudget();

  const [localValorPadrao, setLocalValorPadrao] = React.useState<number>(0);
  const [procInfo, setProcInfo] = React.useState<{
    usa_elemento: boolean;
    qtd_faces: number;
    usa_arcada: boolean;
    usa_hemiarcada: boolean;
  } | null>(null);

  React.useEffect(() => {
    const loadOpcoes = async () => {
      if (!showCategoriasModal || !selectedProc) return;

      setLoadingOpcoes(true);
      try {
        const userId = user?.data?.id || "";
        const response = await getOpcoesValor(userId, selectedProc.id);

        if (response.data?.success) {
          setCategorias(response.data.categorias || []);
          const vp = response.data.valor_padrao || selectedProc.valor;
          setLocalValorPadrao(vp);
          setValorPadrao(vp);

          if (response.data.procedimento) {
            setProcInfo(response.data.procedimento);
            if (selectedProc) {
              selectedProc.usa_elemento = response.data.procedimento.usa_elemento;
              selectedProc.qtd_faces = response.data.procedimento.qtd_faces;
              selectedProc.usa_arcada = response.data.procedimento.usa_arcada;
              selectedProc.usa_hemiarcada = response.data.procedimento.usa_hemiarcada;
            }
          }

          if (!response.data.tem_opcoes) {
            setShowCategoriasModal(false);
            setSelectedCategoria(null);
            setSelectedValor(vp);
            setSelectedCatprocId(0);
            prosseguir(
              response.data.procedimento?.usa_arcada ?? selectedProc.usa_arcada,
              response.data.procedimento?.usa_hemiarcada ?? selectedProc.usa_hemiarcada,
              response.data.procedimento?.usa_elemento ?? selectedProc.usa_elemento
            );
          }
        }
      } catch (error) {
        console.error("Erro ao carregar opções:", error);
        setShowCategoriasModal(false);
        setSelectedValor(selectedProc.valor);
        setSelectedCatprocId(0);
        prosseguir(selectedProc.usa_arcada, selectedProc.usa_hemiarcada, selectedProc.usa_elemento);
      } finally {
        setLoadingOpcoes(false);
      }
    };

    loadOpcoes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCategoriasModal, selectedProc]);

  if (!showCategoriasModal || !selectedProc) return null;

  const prosseguir = (usaArcada: boolean, usaHemiarcada: boolean, usaElemento: boolean) => {
    if (usaArcada || usaHemiarcada) {
      setShowArcadaModal(true);
    } else if (usaElemento) {
      setShowOdontograma(true);
    } else {
      setShowObsModal(true);
    }
  };

  const handleClose = () => {
    setShowCategoriasModal(false);
    setCategorias([]);
    setSelectedCategoria(null);
    setSelectedProc(null);
  };

  const handleSelectPadrao = () => {
    setSelectedCategoria(null);
    setSelectedValor(localValorPadrao);
    setSelectedCatprocId(0);
    setShowCategoriasModal(false);

    const ua = procInfo?.usa_arcada ?? selectedProc.usa_arcada;
    const uh = procInfo?.usa_hemiarcada ?? selectedProc.usa_hemiarcada;
    const ue = procInfo?.usa_elemento ?? selectedProc.usa_elemento;
    setTimeout(() => prosseguir(ua, uh, ue), 100);
  };

  const handleSelectCategoria = (cat: ICategoria) => {
    setSelectedCategoria(cat);
    setSelectedValor(cat.valor);
    setSelectedCatprocId(cat.catproc_id);
    setShowCategoriasModal(false);

    const ua = cat.usa_arcada;
    const uh = cat.usa_hemiarcada;
    const ue = procInfo?.usa_elemento ?? selectedProc.usa_elemento;
    setTimeout(() => prosseguir(ua, uh, ue), 100);
  };

  return (
    <Styled.ModalOverlay onClick={handleClose}>
      <Styled.ModalContent onClick={e => e.stopPropagation()}>
        <Styled.ModalHeader>
          <Styled.CloseButton onClick={handleClose}>
            <IoClose size={24} />
          </Styled.CloseButton>
          <h3>Selecione o Valor</h3>
          <p>{selectedProc.nome}</p>
        </Styled.ModalHeader>

        <Styled.ModalBody>
          {loadingOpcoes ? (
            <Styled.LoadingState>
              <div className="spinner" />
              <p>Carregando opções...</p>
            </Styled.LoadingState>
          ) : (
            <>
              <Styled.GrupoOption isDefault onClick={handleSelectPadrao}>
                <Styled.GrupoHeader>
                  <Styled.GrupoNome isDefault>
                    <IoCheckmarkCircle size={18} color="#10b981" />
                    Procedimento padrão
                  </Styled.GrupoNome>
                  <Styled.GrupoValor showValue={showValues}>
                    {showValues ? `R$ ${formatMoeda(localValorPadrao)}` : '***'}
                  </Styled.GrupoValor>
                </Styled.GrupoHeader>
                <Styled.DefaultBadge>Recomendado</Styled.DefaultBadge>
              </Styled.GrupoOption>

              {categorias.length > 0 && (
                <>
                  <Styled.Divider>
                    <span>Categorias disponíveis</span>
                  </Styled.Divider>

                  {categorias.map(cat => (
                    <Styled.GrupoOption
                      key={cat.catproc_id}
                      onClick={() => handleSelectCategoria(cat)}
                    >
                      <Styled.GrupoHeader>
                        <Styled.GrupoNome>
                          <IoFolderOpen size={16} color="#e67e22" />
                          {cat.nome || 'Sem nome'}
                        </Styled.GrupoNome>
                        <Styled.GrupoValor showValue={showValues}>
                          {showValues ? `R$ ${formatMoeda(cat.valor)}` : '***'}
                        </Styled.GrupoValor>
                      </Styled.GrupoHeader>
                      {cat.obs && (
                        <Styled.GrupoObs>{cat.obs}</Styled.GrupoObs>
                      )}
                    </Styled.GrupoOption>
                  ))}
                </>
              )}
            </>
          )}
        </Styled.ModalBody>
      </Styled.ModalContent>
    </Styled.ModalOverlay>
  );
};

export { BudgetGruposModal };
